import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import toast from "components/partials/toast/toast";

import { getCampaignFolder } from "generated/api/campaign-folder";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useAddTouchpointsFromMondayTemplateContext } from "components/modals/add-touchpoints-from-template-modal/context/add-touchpoints-from-monday-template-context";
import { folderKeys, touchpointKeys } from "hooks/api/query-keys";

import type { AddTouchpointsFromMondayTemplateFormData } from "components/modals/add-touchpoints-from-template-modal/add-touchpoints-from-template-modal.types";

/**
 * Hook for adding touchpoints to an existing folder from Monday.com templates.
 * Used when the modal is opened in "folder context" mode.
 */
export const useCreateTouchpointsInFolder = () => {
  const { campaignTemplates, folderId, setIsOpen, onSuccess } =
    useAddTouchpointsFromMondayTemplateContext();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const queryClient = useQueryClient();

  const createTouchpointsInFolder = useCallback(
    async (formData: AddTouchpointsFromMondayTemplateFormData) => {
      try {
        const clientId = currentClient.id;
        const campaignId = currentCampaign.id;

        if (!clientId || !campaignId || !folderId) {
          throw new Error("Client ID, Campaign ID, or Folder ID is missing");
        }

        const selectedTouchpointIds = formData.templates || {};

        const campaignTemplate = campaignTemplates[0];
        const selectedTouchpointTemplates = (campaignTemplate?.touchpointTemplates || []).filter(
          (tp) => tp.id && selectedTouchpointIds[tp.id] === true,
        );

        const campaignFolderApi = getCampaignFolder();
        await campaignFolderApi.addTouchpointsToFolderFromTemplates(
          { clientId, campaignId, folderId },
          { touchpointTemplates: selectedTouchpointTemplates },
        );

        queryClient.invalidateQueries({ queryKey: folderKeys.all });
        queryClient.invalidateQueries({ queryKey: touchpointKeys.all });

        toast.success({
          data: {
            title: `Added ${selectedTouchpointTemplates.length} creative${selectedTouchpointTemplates.length > 1 ? "s" : ""} to folder`,
          },
        });

        setIsOpen(false);
        onSuccess();
      } catch (error) {
        console.error("Failed to add touchpoints to folder:", error);
        toast.error({
          data: {
            title: "Failed to add creatives to folder",
          },
        });
      }
    },
    [
      campaignTemplates,
      currentClient.id,
      currentCampaign.id,
      folderId,
      setIsOpen,
      onSuccess,
      queryClient,
    ],
  );

  return { createTouchpointsInFolder };
};
